/**
 * Worksheet Builder Types
 *
 * Types for the drag-and-drop worksheet builder.
 * Aligned with the backend template JSON schema (calendar_page.json).
 */

// ============================================================================
// Component Types - What can be placed on a worksheet
// ============================================================================

export type ComponentType =
  | "number_blank"
  | "circle_options"
  | "draw_box"
  | "word_problem"
  | "number_sentence"
  | "tally_marks"
  | "base_ten_blocks"
  | "name"
  | "text_write"
  | "number_write";

export type ComponentCategory =
  | "text"
  | "numbers"
  | "multiple_choice"
  | "drawing"
  | "math";

export interface ComponentDefinition {
  type: ComponentType;
  category: ComponentCategory;
  label: string;
  description: string;
  icon: string; // Lucide icon name
  defaultProps: Partial<WorksheetComponent>;
}

// ============================================================================
// Worksheet Component - An instance on the canvas
// ============================================================================

export interface CropRegion {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface WorksheetComponent {
  id: string;
  type: ComponentType;
  label: string;
  prompt: string;
  instructions?: string;

  // Positioning (relative to canvas, 0-1 range)
  position: {
    x: number; // 0 to 1 (percentage of width)
    y: number; // 0 to 1 (percentage of height)
    width: number; // 0 to 1
    height: number; // 0 to 1
  };

  // Type-specific properties
  options?: string[]; // For circle_options
  answerKey?: string | string[] | number | object;
  answerKeyType?: "static" | "dynamic" | "teacher_review";
  answerKeyDescription?: string;
  validationRule?: string;

  // Extraction config
  extractionPrompt?: string;
  cropRegion?: CropRegion;

  // Grading
  points?: number;
  autoGradable?: boolean;

  // Curriculum alignment
  outcomeIds?: string[];
}

// ============================================================================
// Worksheet - The full document
// ============================================================================

export interface WorksheetLayout {
  pageSize: "letter" | "a4";
  orientation: "portrait" | "landscape";
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  borderStyle?: string;
}

export interface WorksheetGradingConfig {
  totalPoints: number;
  autoGradablePoints: number;
  teacherReviewPoints: number;
  dynamicAnswerKey: boolean;
  requiresDateContext?: boolean;
}

export interface WorksheetTemplate {
  id: string;
  title: string;
  description?: string;
  grade: string[];
  subject: string;
  templateType?: string;
  outcomeIds: string[];
  difficulty?: number;
  estimatedMinutes?: number;
  usageFrequency?: "daily" | "weekly" | "monthly" | "once";
  teacherNotes?: string;

  components: WorksheetComponent[];
  layout: WorksheetLayout;
  gradingConfig?: WorksheetGradingConfig;

  // Metadata
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  status?: "draft" | "published";
}

// ============================================================================
// Component Palette - Available components to drag
// ============================================================================

export const COMPONENT_DEFINITIONS: ComponentDefinition[] = [
  // Text category
  {
    type: "name",
    category: "text",
    label: "Name Field",
    description: "Student name input line",
    icon: "User",
    defaultProps: {
      label: "Name",
      prompt: "Name:",
      instructions: "Write your name on the line.",
      autoGradable: false,
      points: 0,
      position: { x: 0.7, y: 0, width: 0.28, height: 0.05 },
    },
  },
  {
    type: "text_write",
    category: "text",
    label: "Text Response",
    description: "Open text answer field",
    icon: "Type",
    defaultProps: {
      label: "Response",
      prompt: "Write your answer:",
      autoGradable: false,
      points: 1,
      position: { x: 0, y: 0, width: 0.4, height: 0.1 },
    },
  },
  {
    type: "word_problem",
    category: "text",
    label: "Word Problem",
    description: "Text problem with answer blank",
    icon: "FileText",
    defaultProps: {
      label: "Word Problem",
      prompt: "Solve the problem:",
      autoGradable: true,
      points: 2,
      position: { x: 0, y: 0, width: 0.9, height: 0.15 },
    },
  },

  // Numbers category
  {
    type: "number_blank",
    category: "numbers",
    label: "Number Blank",
    description: "Single number input blank",
    icon: "Hash",
    defaultProps: {
      label: "Number",
      prompt: "Write the number:",
      autoGradable: true,
      points: 1,
      position: { x: 0, y: 0, width: 0.15, height: 0.08 },
    },
  },
  {
    type: "number_write",
    category: "numbers",
    label: "Number Write",
    description: "Number writing with extraction",
    icon: "Binary",
    defaultProps: {
      label: "Number",
      prompt: "Write the number:",
      extractionPrompt: "Extract the number written. Return only the number.",
      autoGradable: true,
      points: 1,
      position: { x: 0, y: 0, width: 0.2, height: 0.08 },
    },
  },

  // Multiple choice category
  {
    type: "circle_options",
    category: "multiple_choice",
    label: "Circle Choice",
    description: "Multiple options - circle the answer",
    icon: "Circle",
    defaultProps: {
      label: "Circle Choice",
      prompt: "Circle the correct answer:",
      options: ["Option A", "Option B", "Option C", "Option D"],
      autoGradable: true,
      points: 1,
      position: { x: 0, y: 0, width: 0.4, height: 0.1 },
    },
  },

  // Drawing category
  {
    type: "draw_box",
    category: "drawing",
    label: "Drawing Box",
    description: "Area for student drawing",
    icon: "Pencil",
    defaultProps: {
      label: "Drawing",
      prompt: "Draw your answer:",
      autoGradable: false,
      points: 1,
      answerKeyType: "teacher_review",
      position: { x: 0, y: 0, width: 0.4, height: 0.2 },
    },
  },

  // Math category
  {
    type: "number_sentence",
    category: "math",
    label: "Number Sentence",
    description: "Math equation format (___ + ___ = ___)",
    icon: "Calculator",
    defaultProps: {
      label: "Number Sentence",
      prompt: "___ + ___ = ___",
      extractionPrompt:
        'Extract the three numbers in the equation. Return JSON: {"addend1": X, "addend2": Y, "sum": Z}',
      autoGradable: true,
      points: 2,
      position: { x: 0, y: 0, width: 0.45, height: 0.1 },
    },
  },
  {
    type: "tally_marks",
    category: "math",
    label: "Tally Marks",
    description: "Tally counting area",
    icon: "BarChart2",
    defaultProps: {
      label: "Tallies",
      prompt: "Draw tally marks:",
      extractionPrompt:
        "Count the tally marks drawn. Groups of 5 (with diagonal) count as 5. Return the total count.",
      autoGradable: true,
      points: 1,
      position: { x: 0, y: 0, width: 0.4, height: 0.15 },
    },
  },
  {
    type: "base_ten_blocks",
    category: "math",
    label: "Base Ten Blocks",
    description: "Place value visual representation",
    icon: "Grid3X3",
    defaultProps: {
      label: "Base Ten",
      prompt: "Color the base ten blocks:",
      extractionPrompt:
        'Count the colored base ten blocks. Return JSON: {"tens": X, "ones": Y, "total": Z}',
      autoGradable: true,
      points: 2,
      position: { x: 0, y: 0, width: 0.9, height: 0.25 },
    },
  },
];

// ============================================================================
// Category Metadata
// ============================================================================

export interface CategoryInfo {
  id: ComponentCategory;
  label: string;
  icon: string;
  color: string;
}

export const CATEGORIES: CategoryInfo[] = [
  { id: "text", label: "Text", icon: "Type", color: "text-purple-500" },
  { id: "numbers", label: "Numbers", icon: "Hash", color: "text-blue-500" },
  {
    id: "multiple_choice",
    label: "Multiple Choice",
    icon: "Circle",
    color: "text-green-500",
  },
  { id: "drawing", label: "Drawing", icon: "Pencil", color: "text-orange-500" },
  { id: "math", label: "Math", icon: "Calculator", color: "text-red-500" },
];

// ============================================================================
// Helper Functions
// ============================================================================

export function getComponentDefinition(
  type: ComponentType
): ComponentDefinition | undefined {
  return COMPONENT_DEFINITIONS.find((def) => def.type === type);
}

export function getComponentsByCategory(
  category: ComponentCategory
): ComponentDefinition[] {
  return COMPONENT_DEFINITIONS.filter((def) => def.category === category);
}

export function generateComponentId(): string {
  return `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function createComponent(
  definition: ComponentDefinition,
  position?: { x: number; y: number }
): WorksheetComponent {
  const id = generateComponentId();
  return {
    id,
    type: definition.type,
    label: definition.defaultProps.label || definition.label,
    prompt: definition.defaultProps.prompt || "",
    ...definition.defaultProps,
    position: position
      ? {
          ...definition.defaultProps.position!,
          x: position.x,
          y: position.y,
        }
      : definition.defaultProps.position!,
  };
}

// ============================================================================
// Default Worksheet Template
// ============================================================================

export function createEmptyWorksheet(): WorksheetTemplate {
  return {
    id: "",
    title: "Untitled Worksheet",
    description: "",
    grade: ["1"],
    subject: "mathematics",
    outcomeIds: [],
    difficulty: 1,
    estimatedMinutes: 10,
    components: [],
    layout: {
      pageSize: "letter",
      orientation: "portrait",
      margins: { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 },
    },
    gradingConfig: {
      totalPoints: 0,
      autoGradablePoints: 0,
      teacherReviewPoints: 0,
      dynamicAnswerKey: false,
    },
    status: "draft",
  };
}
